import { Injectable } from "@angular/core";
import { AngularFirestore } from "@angular/fire/firestore";
import jsPDF from "jspdf";
import { PDFProps } from "src/app/client-dashboard/client-event-dashboard/client-modules/certificate/types/certificate";

@Injectable({
    providedIn: "root",
})
export class DbCertificateProvider {
    constructor(private aFirestore: AngularFirestore) {}

    /**
     * Retrieves a specific module from the Firestore database based on the provided event and module IDs.
     *
     * @param eventId - The unique identifier of the event.
     * @param moduleId - The unique identifier of the module within the event.
     *
     * @returns A Promise that resolves to a DocumentSnapshot containing the module data.
     */
    getModule(eventId: string, moduleId: string) {
        const ref = this.aFirestore.firestore.doc(
            `events/${eventId}/modules/${moduleId}`
        );

        return ref.get();
    }

    /**
     * Updates the PDF properties in the Firestore database.
     *
     * @param eventId - The unique identifier of the event.
     * @param moduleId - The unique identifier of the module within the event.
     * @param pdfProps - The updated PDF properties to be saved.
     *
     * @returns A Promise that resolves when the update operation is successfully completed.
     */
    saveCertificatePDFProps(eventId: string, moduleId: string, pdfProps: PDFProps) {
        const ref = this.aFirestore.firestore.doc(
            `events/${eventId}/modules/${moduleId}`
        );

        return ref.update({ pdfProps });
    }

    /**
     * Generates a PDF output for a certificate using the provided PDF properties and attendee name.
     *
     * @param pdfProps - The properties for the PDF, including image source, dimensions, scale, and rectangle dimension/position for text placement.
     * @param attendeeName - The name of the attendee to be printed on the certificate.
     *
     * @returns A Blob URL representing the generated PDF.
     */
    generateCertificatePDFOutput(pdfProps: PDFProps, attendeeName: string) {
        const { imgSrc, imgWidth, imgHeight, scale, rectangle } = pdfProps;
        const pdf = new jsPDF({
            orientation: "landscape",
            unit: "pt",
            format: [imgWidth, imgHeight],
        });

        pdf.addImage(imgSrc, "JPEG", 0, 0, imgWidth, imgHeight);

        const { x, y, width, height, color, fontSize } = rectangle;

        const textX = (x + width / 2) * scale;
        const textY = (y + height / 2) * scale;

        pdf.setFontSize(fontSize);
        pdf.setTextColor(color);
        pdf.text(attendeeName, textX, textY, {
            align: "center",
            baseline: "middle",
        });

        return pdf.output("bloburl");
    }
}
